import React, { useState, useEffect } from "react";
import axios from "axios";
import { formatDateFR } from "../lib/utils";
import { 
  formatDateISO, 
  getMonday, 
  getWeekDays, 
  getToday, 
  addDays,
  formatDateFrench,
  formatDateLongFrench,
  getMonthNameFrench,
  getDayNameFrench,
  getTodayParis
} from "../utils/dateUtils";
import ConfirmModal from "./ConfirmModal";
import Toast from "./Toast";
import RecurringMissionModal from "./RecurringMissionModal";
import { toast } from 'sonner';
import { getEmployeeCardStyle } from "../utils/employeeColors";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL}/api` : window.location.origin + '/api';

const WeeklyTimeline = () => {
  const [missions, setMissions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [housings, setHousings] = useState([]);
  const [clients, setClients] = useState([]);
  // Initialiser currentWeekStart à J-1 (hier) pour afficher J-1 à J+9
  const [currentWeekStart, setCurrentWeekStart] = useState(() => addDays(getTodayParis(), -1));
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedHour, setSelectedHour] = useState(null);
  const [formData, setFormData] = useState({
    is_divers: false,
    housing_id: "",
    employee_id: "",
    date_mission: "",
    notes: "",
    prix: "",
    prix_intervenant: ""
  });
  const [editingMission, setEditingMission] = useState(null);
  
  // États pour le modal de confirmation
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {}
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [editAction, setEditAction] = useState(null); // 'edit', 'delete', 'move'
  const [moveToDate, setMoveToDate] = useState("");
  
  // État pour les toasts
  const [toast, setToast] = useState(null);
  
  // État pour missions récurrentes
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  
  // Fonction pour afficher un toast
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };
  const [editedNotes, setEditedNotes] = useState("");
  const [expandedMonths, setExpandedMonths] = useState(new Set());
  
  // Filtres mois/année - Initialisés au mois et année en cours
  const [filterMonth, setFilterMonth] = useState(() => new Date().getMonth().toString());
  const [filterYear, setFilterYear] = useState(() => new Date().getFullYear().toString());
  
  // État pour masquer/afficher les mois antérieurs
  const [showPastMonths, setShowPastMonths] = useState(true); // Par défaut: afficher tous les mois
  
  // Ref pour scroll vers aujourd'hui
  const todayRef = React.useRef(null);
  
  // Ref pour chaque date (pour scroll après ajout de mission)
  const dateRefs = React.useRef({});
  
  // Date sélectionnée pour garder la position après ajout
  const [lastSelectedDate, setLastSelectedDate] = useState(null);
  
  // Ref pour le conteneur de scroll et sauvegarde de la position
  const scrollContainerRef = React.useRef(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  
  // État pour détecter l'orientation mobile
  const [isMobilePortrait, setIsMobilePortrait] = useState(false);
  const [showRotateHint, setShowRotateHint] = useState(true);
  
  // Détecter l'orientation et la taille de l'écran
  useEffect(() => {
    const checkOrientation = () => {
      const isMobile = window.innerWidth < 768;
      const isPortrait = window.innerHeight > window.innerWidth;
      setIsMobilePortrait(isMobile && isPortrait);
    };
    
    checkOrientation();
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);
    
    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, []);

  // Fonction pour scroller vers la gauche
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -640, // 4 colonnes de 160px
        behavior: 'smooth'
      });
    }
  };

  // Fonction pour scroller vers la droite
  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 640, // 4 colonnes de 160px
        behavior: 'smooth'
      });
    }
  };

  // Mettre à jour les états de scroll
  const updateScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  // Écouter les événements de scroll
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', updateScrollButtons);
      updateScrollButtons();
      return () => container.removeEventListener('scroll', updateScrollButtons);
    }
  }, [missions]);

  // Obtenir les jours à afficher : MOIS COMPLET basé sur filterMonth et filterYear
  const getWeekDaysLocal = () => {
    const days = [];
    
    // Utiliser le mois et l'année du filtre
    const month = parseInt(filterMonth);
    const year = parseInt(filterYear);
    
    // Premier jour du mois
    const firstDay = new Date(year, month, 1);
    // Dernier jour du mois
    const lastDay = new Date(year, month + 1, 0);
    
    // Ajouter tous les jours du mois
    for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
      days.push(new Date(d));
    }
    
    return days;
  };

  // Grouper les jours par mois
  const groupDaysByMonth = (days) => {
    const grouped = {};
    days.forEach(date => {
      const monthIndex = date.getMonth();
      const monthKey = `${date.getFullYear()}-${monthIndex}`;
      if (!grouped[monthKey]) {
        grouped[monthKey] = {
          monthName: monthNames[monthIndex],
          monthIndex: monthIndex,
          year: date.getFullYear(),
          days: []
        };
      }
      grouped[monthKey].days.push(date);
    });
    return Object.values(grouped);
  };

  const toggleMonth = (monthKey) => {
    const newExpanded = new Set(expandedMonths);
    if (newExpanded.has(monthKey)) {
      newExpanded.delete(monthKey);
    } else {
      newExpanded.add(monthKey);
    }
    setExpandedMonths(newExpanded);
  };

  // Expand all months by default on load
  useEffect(() => {
    const weekDays = getWeekDaysLocal();
    const monthsInWeek = new Set(
      weekDays.map(date => `${date.getFullYear()}-${date.getMonth()}`)
    );
    setExpandedMonths(monthsInWeek);
  }, [filterMonth, filterYear]);

  // Les noms de jours et mois sont maintenant gérés par dateUtils.js avec les fonctions françaises
  const dayNames = ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"];
  const monthNames = [
    "janvier", "février", "mars", "avril", "mai", "juin",
    "juillet", "août", "septembre", "octobre", "novembre", "décembre"
  ];

  useEffect(() => {
    loadData();
  }, [filterMonth, filterYear]); // Recharger quand le filtre change
  
  // Scroll vers aujourd'hui ou la dernière date sélectionnée après chargement
  useEffect(() => {
    if (!loading && missions.length >= 0) {
      setTimeout(() => {
        // Si une date était sélectionnée, y retourner
        if (lastSelectedDate && dateRefs.current[lastSelectedDate]) {
          dateRefs.current[lastSelectedDate].scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        } 
        // Sinon, aller à aujourd'hui
        else if (todayRef.current) {
          todayRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }
      }, 300);
    }
  }, [loading, filterMonth, filterYear]);

  // Fonction wrapper pour recharger les données en conservant la position de scroll
  const reloadDataKeepingScroll = async (keepDate = null) => {
    // Sauvegarder la date sélectionnée
    if (keepDate) {
      setLastSelectedDate(keepDate);
    }
    
    // Recharger les données
    await loadData();
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const month = parseInt(filterMonth);
      const year = parseInt(filterYear);
      const firstDay = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const lastDayDate = new Date(year, month + 1, 0);
      const lastDay = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDayDate.getDate()).padStart(2, '0')}`;

      const [employeesRes, housingsRes, clientsRes, missionsRes] = await Promise.all([
        axios.get(`${API}/employees`),
        axios.get(`${API}/housings`),
        axios.get(`${API}/clients`),
        axios.get(`${API}/missions?start_date=${firstDay}&end_date=${lastDay}`)
      ]);
      
      setEmployees(employeesRes.data);
      setHousings(housingsRes.data);
      setClients(clientsRes.data);
      setMissions(missionsRes.data || []);
    } catch (error) {
      console.error("Erreur lors du chargement des données:", error);
    } finally {
      setLoading(false);
    }
  };

  const navigateWeek = (direction) => {
    // Navigation par 7 jours (1 semaine) pour une navigation fluide
    const newWeekStart = addDays(currentWeekStart, direction * 7);
    setCurrentWeekStart(newWeekStart);
  };

  const goToToday = () => {
    // Afficher le mois en cours - commencer au 1er du mois
    const today = getTodayParis();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    // Effacer la dernière date sélectionnée pour que l'useEffect post-load scrolle vers aujourd'hui
    setLastSelectedDate(null);

    // Synchroniser le filtre mois/année avec aujourd'hui (déclenche loadData si changement)
    const todayMonth = String(today.getMonth());
    const todayYear = String(today.getFullYear());
    const filterChanges = (filterMonth !== todayMonth) || (filterYear !== todayYear);
    if (filterChanges) {
      setFilterMonth(todayMonth);
      setFilterYear(todayYear);
    }
    setCurrentWeekStart(firstDayOfMonth);

    // Scroll vers aujourd'hui après le render.
    // Si le filtre change, loadData se relance et l'useEffect post-chargement scrollera.
    // Sinon on force le scroll ici avec un petit délai pour laisser React re-render.
    if (!filterChanges) {
      setTimeout(() => {
        if (todayRef.current) {
          todayRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      }, 150);
    }
  };
  
  const applyMonthYearFilter = () => {
    if (!filterMonth || !filterYear) {
      showToast("Veuillez sélectionner un mois et une année", "error");
      return;
    }
    
    // Créer une date pour le premier jour du mois sélectionné
    const firstDayOfMonth = new Date(parseInt(filterYear), parseInt(filterMonth), 1);
    firstDayOfMonth.setHours(12, 0, 0, 0);
    
    // Calculer le nombre de jours depuis ce premier jour jusqu'à aujourd'hui
    const today = getTodayParis();
    const daysDiff = Math.floor((today - firstDayOfMonth) / (1000 * 60 * 60 * 24));
    
    if (daysDiff < 0) {
      showToast("Le mois sélectionné est dans le futur", "error");
      return;
    }
    
    // Ajuster le nombre de jours à afficher (au minimum 2, au maximum 365)
    const maxDays = 365;
    const newDaysToShow = Math.min(Math.max(daysDiff + 1, 2), maxDays);
    // setDaysToShow supprimé (état non défini - variable orpheline)
    void newDaysToShow;
    
    console.log('Filter applied:', {
      selectedMonth: filterMonth,
      selectedYear: filterYear,
      firstDayOfMonth: formatDateISO(firstDayOfMonth),
      daysDiff,
      newDaysToShow
    });
    
    showToast(`Affichage des ${newDaysToShow} derniers jours jusqu'à aujourd'hui`, "success");
  };

  const handleOpenModal = (date) => {
    // Sauvegarder la position de scroll avant d'ouvrir le modal
    if (scrollContainerRef.current) {
      setScrollPosition(scrollContainerRef.current.scrollLeft);
    }
    
    // Extraire directement les composants de la date (déjà correcte depuis getWeekDays)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    console.log('handleOpenModal - date:', date, 'dateStr:', dateStr);
    setSelectedDate(dateStr);
    
    setFormData({
      is_divers: false,
      housing_id: "",
      employee_id: "",
      date_mission: dateStr,
      notes: "",
      prix: "",
      prix_intervenant: ""
    });
    setShowModal(true);
  };

  const handleEditMission = (missionData) => {
    setEditingMission(missionData);
    setEditAction('edit');
    setMoveToDate(missionData.mission.date_mission);
    setEditedNotes(missionData.mission.notes || "");
    setShowEditModal(true);
  };

  const handleDeleteMission = async (missionId) => {
    console.log('handleDeleteMission called with ID:', missionId);
    
    if (!missionId) {
      showToast("Erreur : ID de mission manquant", "error");
      return;
    }
    
    {  // Action directe sans confirmation
      try {
        console.log('Attempting to delete mission:', missionId);
        
        // Sauvegarder la position avant suppression
        if (scrollContainerRef.current) {
          setScrollPosition(scrollContainerRef.current.scrollLeft);
        }
        
        const response = await axios.delete(`${API}/missions/${missionId}`);
        console.log('Delete response:', response);
        
        setShowEditModal(false);
        await reloadDataKeepingScroll();
        
        // Restaurer la position après le rechargement
        setTimeout(() => {
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollLeft = scrollPosition;
          }
        }, 100);
        
        showToast("Mission supprimée définitivement avec succès", "success");
      } catch (error) {
        console.error("Erreur lors de la suppression:", error);
        showToast("Erreur lors de la suppression: " + (error.response?.data?.detail || error.message), "error");
      }
    }
  };

  const handleMoveMission = async () => {
    if (!moveToDate) {
      showToast("Veuillez sélectionner une date", "error");
      return;
    }
    
    try {
      await axios.put(`${API}/missions/${editingMission.mission.id}`, {
        date_mission: moveToDate
      });
      showToast("Mission déplacée avec succès", "success");
      setShowEditModal(false);
      await reloadDataKeepingScroll();
    } catch (error) {
      if (error.moveCancelled) return;
      console.error("Erreur lors du déplacement:", error);
      showToast("Erreur lors du déplacement", "error");
    }
  };

  const handleSaveNotes = async () => {
    try {
      await axios.put(`${API}/missions/${editingMission.mission.id}`, {
        notes: editedNotes
      });
      showToast("Notes mises à jour avec succès", "success");
      setShowEditModal(false);
      await reloadDataKeepingScroll();
    } catch (error) {
      console.error("Erreur lors de la mise à jour des notes:", error);
      showToast("Erreur lors de la mise à jour des notes", "error");
    }
  };

  const handleEmployeeChange = async (missionId, newEmployeeId) => {
    try {
      // Trouver la mission à réassigner
      const missionToReassign = missions.find(m => m.mission.id === missionId);
      
      if (!missionToReassign) {
        showToast("Mission introuvable", "error");
        return;
      }
      
      // Vérifier si le logement a des intervenants exclusifs
      if (missionToReassign.housing && missionToReassign.housing.intervenants_exclusifs && missionToReassign.housing.intervenants_exclusifs.length > 0 && newEmployeeId) {
        const employee = employees.find(e => e.id === newEmployeeId);
        if (employee) {
          const employeeName = `${employee.prenom} ${employee.nom}`.trim();
          const isAuthorized = missionToReassign.housing.intervenants_exclusifs.some(intervenant => 
            employeeName.toLowerCase().includes(intervenant.toLowerCase()) || 
            intervenant.toLowerCase().includes(employee.prenom.toLowerCase())
          );
          
          if (!isAuthorized) {
            // Afficher le modal de confirmation au lieu de window.confirm
            setConfirmModal({
              isOpen: true,
              title: "Avertissement - Intervenants Exclusifs",
              message: `Ce logement est exclusif aux intervenants suivants :\n\n${missionToReassign.housing.intervenants_exclusifs.join(", ")}\n\nVous essayez d'assigner ${employeeName}.\n\nVoulez-vous continuer quand même ?`,
              onConfirm: async () => {
                await performEmployeeChange(missionId, newEmployeeId);
              }
            });
            return; // Attendre la confirmation de l'utilisateur
          }
        }
      }
      
      // Si pas d'intervenants exclusifs ou autorisé, effectuer le changement directement
      await performEmployeeChange(missionId, newEmployeeId);
      
    } catch (error) {
      console.error("Erreur lors de l'assignation:", error);
      showToast("Erreur lors de l'assignation de l'employée", "error");
    }
  };
  
  // Fonction pour effectuer réellement le changement d'employé
  const performEmployeeChange = async (missionId, newEmployeeId) => {
    try {
      await axios.put(`${API}/missions/${missionId}`, {
        employee_id: newEmployeeId || null
      });
      await reloadDataKeepingScroll();
    } catch (error) {
      console.error("Erreur lors de l'assignation:", error);
      showToast("Erreur lors de l'assignation de l'employée", "error");
    }
  };

  const handleHousingChange = (housingId) => {
    setFormData(prev => ({...prev, housing_id: housingId}));
    
    if (housingId) {
      const housing = housings.find(h => h.id === housingId);
      if (housing) {
        setFormData(prev => ({
          ...prev,
          housing_id: housingId,
          heure_debut: housing.creneau_debut || prev.heure_debut,
          heure_fin: housing.creneau_fin || prev.heure_fin,
          prix: housing.prix_mm_conciergerie ? housing.prix_mm_conciergerie.toString() : prev.prix,
          prix_intervenant: housing.prix_intervenant ? housing.prix_intervenant.toString() : prev.prix_intervenant
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      heure_debut: "09:00", // Heure par défaut
      heure_fin: "11:00", // Heure par défaut
      prix: formData.prix ? parseFloat(formData.prix) : null,
      prix_intervenant: formData.prix_intervenant ? parseFloat(formData.prix_intervenant) : null
    };
    // Si c'est un événement divers, housing_id peut être null
    if (formData.is_divers) {
      submitData.housing_id = null;
    }
    try {
      // Vérifier si le logement a des intervenants exclusifs
      if (!formData.is_divers && formData.housing_id && formData.employee_id) {
        const selectedHousing = housings.find(h => h.id === formData.housing_id);
        const selectedEmployee = employees.find(e => e.id === formData.employee_id);
        
        if (selectedHousing && selectedHousing.intervenants_exclusifs && selectedHousing.intervenants_exclusifs.length > 0 && selectedEmployee) {
          const employeeName = `${selectedEmployee.prenom} ${selectedEmployee.nom}`.trim();
          const isAuthorized = selectedHousing.intervenants_exclusifs.some(intervenant => 
            employeeName.toLowerCase().includes(intervenant.toLowerCase()) || 
            intervenant.toLowerCase().includes(selectedEmployee.prenom.toLowerCase())
          );
          
          if (!isAuthorized) {
            toast.warning(`Ce logement est exclusif à : ${selectedHousing.intervenants_exclusifs.join(", ")}`);
          }
        }
      }

      await axios.post(`${API}/missions`, submitData);
      setShowModal(false);
      // Après création : retour à aujourd'hui (goToToday s'occupe du reload si besoin + scroll)
      setLastSelectedDate(null);
      await reloadDataKeepingScroll();
      goToToday();

      showToast("Événement créé avec succès", "success");
    } catch (error) {
      console.error("Erreur lors de la création:", error);
      
      // Gestion spéciale du code 409 (doublon détecté)
      if (error.response?.status === 409 && error.response?.data?.detail?.can_force) {
        const detail = error.response.data.detail;
        const confirmMsg = `⚠️ ATTENTION : Une mission existe déjà pour "${detail.housing_pseudo}" à cette date.\n\n` +
                          `Voulez-vous quand même créer cette nouvelle mission ?\n\n` +
                          `(Cette vérification ne s'applique qu'aux clients particuliers)`;
        
        {  // Action directe sans confirmation
          // Réessayer avec force=true
          try {
            await axios.post(`${API}/missions?force=true`, submitData);
            setShowModal(false);
            setLastSelectedDate(null);
            await reloadDataKeepingScroll();
            goToToday();

            showToast("Événement créé avec succès (doublon forcé)", "success");
          } catch (retryError) {
            console.error("Erreur lors de la création forcée:", retryError);
            showToast("Erreur lors de la création: " + (retryError.response?.data?.detail || retryError.message), "error");
          }
        }
      } else {
        showToast("Erreur lors de la création: " + (error.response?.data?.detail || error.message), "error");
      }
    }
  };

  // Fonction pour créer des missions récurrentes
  const handleCreateRecurringMissions = async (recurringData) => {
    try {
      const response = await axios.post(`${API}/missions/recurring`, recurringData);
      showToast(`${response.data.created_count} missions récurrentes créées avec succès`, "success");
      setShowRecurringModal(false);
      setLastSelectedDate(null);
      await reloadDataKeepingScroll();
      goToToday();
    } catch (error) {
      console.error("Erreur lors de la création des missions récurrentes:", error);
      showToast("Erreur lors de la création des missions récurrentes: " + (error.response?.data?.detail || error.message), "error");
    }
  };

  // Obtenir les missions pour une date spécifique (triées par ordre de création)
  const getMissionsForDate = (date) => {
    // Formatter la date en YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    console.log('🔍 Recherche missions pour:', dateStr);
    console.log('📊 Total missions disponibles:', missions.length);
    
    // Filtrer les missions
    const filtered = missions.filter(missionData => {
      if (!missionData || !missionData.mission) {
        console.log('⚠️ Mission invalide détectée');
        return false;
      }
      
      let missionDate = missionData.mission.date_mission;
      
      // Si c'est une string, extraire juste la date
      if (typeof missionDate === 'string') {
        missionDate = missionDate.split('T')[0];
      }
      
      // Exclure les missions annulées
      if (missionData.mission.status === 'annule') return false;
      
      const isMatch = missionDate === dateStr;
      return isMatch;
    }).sort((a, b) => {
      // Regrouper par employée : Mag/Marion → Ben → Autres
      const getEmployeePriority = (missionData) => {
        if (!missionData.employee) return 99;
        const name = `${missionData.employee.prenom || ''} ${missionData.employee.nom || ''}`.toLowerCase();
        
        // DIVERS en premier
        if (!missionData.housing) return 0;
        
        // Mag et Marion en premier
        if (name.includes('mag') || name.includes('marion')) return 1;
        
        // Ben ensuite
        if (name.includes('ben')) return 2;
        
        // Autres employées après
        return 3;
      };
      
      const priorityA = getEmployeePriority(a);
      const priorityB = getEmployeePriority(b);
      
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      
      // Même employée : trier par nom de l'employée puis par heure
      const nameA = `${a.employee?.prenom || ''} ${a.employee?.nom || ''}`;
      const nameB = `${b.employee?.prenom || ''} ${b.employee?.nom || ''}`;
      if (nameA !== nameB) return nameA.localeCompare(nameB);
      
      const heureA = a.mission?.heure_debut || '00:00';
      const heureB = b.mission?.heure_debut || '00:00';
      return heureA.localeCompare(heureB);
    });
    
    console.log(`✅ ${filtered.length} mission(s) trouvée(s) pour ${dateStr} (après filtrage)`);
    return filtered;
  };
  
  // Calculer le nombre maximum de missions pour un jour de la semaine
  const getMaxMissionsInWeek = () => {
    const weekDays = getWeekDaysLocal();
    let maxCount = 1; // Au moins 1 pour afficher une colonne
    
    weekDays.forEach(date => {
      const dayMissions = getMissionsForDate(date);
      if (dayMissions.length > maxCount) {
        maxCount = dayMissions.length;
      }
    });
    
    return Math.max(maxCount, 3); // Au moins 3 colonnes minimum
  };

  const weekDays = getWeekDaysLocal();
  const weekStartMonth = monthNames[currentWeekStart.getMonth()];
  const weekStartYear = currentWeekStart.getFullYear();
  const maxMissionsPerDay = getMaxMissionsInWeek(); // Dynamique
  const missionSlots = Array.from({ length: maxMissionsPerDay }, (_, i) => i + 1);
  
  // Obtenir la date d'aujourd'hui pour comparaison (safe)
  const todayStr = getToday().toDateString();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading-spinner"></div>
        <span className="ml-2 text-gray-600">Chargement du planning...</span>
      </div>
    );
  }

  return (
    <div className="max-w-full mx-auto py-3 md:py-6 px-2 md:px-4" style={{ fontFamily: "'Poppins', sans-serif" }} data-testid="weekly-timeline">
      
      {/* Bannière invitation à tourner le téléphone - Mobile Portrait uniquement */}
      {isMobilePortrait && showRotateHint && (
        <div className="fixed inset-0 bg-slate-900/95 z-50 flex flex-col items-center justify-center p-6 text-center">
          <div className="animate-bounce mb-6">
            <svg 
              className="w-24 h-24 text-amber-400" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="1.5"
            >
              {/* Icône téléphone avec flèche de rotation */}
              <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
              <line x1="12" y1="18" x2="12" y2="18.01" strokeWidth="2" strokeLinecap="round" />
              <path d="M20 12l2-2m0 0l2 2m-2-2v6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="text-amber-400 text-xl font-bold mb-3">
            📱 Tournez votre téléphone
          </div>
          <p className="text-white text-sm mb-6 max-w-xs">
            Pour une meilleure expérience du planning, passez en mode paysage (horizontal)
          </p>
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-20 border-2 border-slate-500 rounded-lg flex items-center justify-center">
              <span className="text-slate-500 text-xs">📱</span>
            </div>
            <span className="text-amber-400 text-2xl">→</span>
            <div className="w-20 h-12 border-2 border-amber-400 rounded-lg flex items-center justify-center">
              <span className="text-amber-400 text-xs">📱</span>
            </div>
          </div>
          <button
            onClick={() => setShowRotateHint(false)}
            className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold px-6 py-3 rounded-lg transition-colors"
          >
            Continuer quand même
          </button>
        </div>
      )}
      
      {/* Header - Responsive */}
      <div className="mb-4 md:mb-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">📅 Planning</h1>
            <p className="text-gray-600 mt-1 text-xs md:text-sm">
              Vue des missions par jour
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 md:space-x-3">
            {/* Filtre mois/année - Compact sur mobile */}
            <div className="flex items-center gap-1 md:space-x-2 bg-white border border-gray-300 rounded-lg px-2 py-1.5 md:px-3 md:py-2">
              <span className="text-xs md:text-sm text-gray-600 hidden md:inline">Filtre:</span>
              <select
                value={filterMonth}
                onChange={(e) => setFilterMonth(e.target.value)}
                className="text-xs md:text-sm border border-gray-300 rounded px-1 md:px-2 py-0.5 md:py-1"
              >
                <option value="">Mois</option>
                {monthNames.map((month, idx) => (
                  <option key={idx} value={idx}>
                    {month.charAt(0).toUpperCase() + month.slice(1, 4)}
                  </option>
                ))}
              </select>
              <select
                value={filterYear}
                onChange={(e) => setFilterYear(e.target.value)}
                className="text-xs md:text-sm border border-gray-300 rounded px-1 md:px-2 py-0.5 md:py-1"
              >
                <option value="">Année</option>
                {[2024, 2025, 2026, 2027].map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              <button
                onClick={applyMonthYearFilter}
                className="text-xs md:text-sm bg-blue-600 text-white px-2 md:px-3 py-0.5 md:py-1 rounded hover:bg-blue-700"
              >
                ✓
              </button>
            </div>
            
            <button
              onClick={goToToday}
              className="btn-secondary text-xs md:text-sm px-2 md:px-3 py-1"
            >
              📍 <span className="hidden sm:inline">Aujourd'hui</span>
            </button>
          </div>
        </div>
      </div>

      {/* En-tête avec navigation et contrôle d'affichage des mois - COMPACT & RESPONSIVE */}
      <div className="mb-3 md:mb-4">
        <div className="bg-gradient-to-r from-slate-800 via-slate-900 to-slate-800 rounded-lg md:rounded-xl shadow-xl p-2 md:p-4 border border-slate-700">
          <div className="flex flex-col items-center max-w-6xl mx-auto space-y-2 md:space-y-3">
            
            {/* Boutons de navigation et titre - Mobile Friendly */}
            <div className="flex justify-between items-center w-full gap-2 md:gap-4">
              <button
                onClick={() => {
                  const newMonth = parseInt(filterMonth) - 1;
                  if (newMonth < 0) {
                    setFilterMonth('11');
                    setFilterYear(String(parseInt(filterYear) - 1));
                  } else {
                    setFilterMonth(String(newMonth));
                  }
                }}
                className="group px-2 md:px-4 py-1.5 md:py-2 text-xs md:text-sm font-semibold text-white hover:text-amber-300 rounded-lg bg-slate-800/50 hover:bg-slate-700/80 transition-all duration-300 uppercase tracking-wider border border-slate-600 hover:border-amber-600/50 shadow-md hover:shadow-lg"
              >
                <span className="inline-block transition-transform duration-300 group-hover:-translate-x-1">←</span> <span className="hidden sm:inline">Mois </span>Préc.
              </button>
              
              <div className="flex flex-col items-center">
                <h2 className="text-base md:text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-amber-300 to-amber-400 tracking-tight" style={{ 
                  letterSpacing: '-0.02em',
                  fontWeight: '700'
                }}>
                  {monthNames[parseInt(filterMonth)].substring(0, 3).toUpperCase()}. {filterYear}
                </h2>
                <span className="text-[10px] md:text-xs text-slate-200 font-light tracking-wider hidden sm:block">
                  Mois complet • Jour actuel surligné
                </span>
              </div>
              
              <button
                onClick={() => {
                  const newMonth = parseInt(filterMonth) + 1;
                  if (newMonth > 11) {
                    setFilterMonth('0');
                    setFilterYear(String(parseInt(filterYear) + 1));
                  } else {
                    setFilterMonth(String(newMonth));
                  }
                }}
                className="group px-2 md:px-4 py-1.5 md:py-2 text-xs md:text-sm font-semibold text-white hover:text-amber-300 rounded-lg bg-slate-800/50 hover:bg-slate-700/80 transition-all duration-300 uppercase tracking-wider border border-slate-600 hover:border-amber-600/50 shadow-md hover:shadow-lg"
              >
                <span className="hidden sm:inline">Mois </span>Suiv. <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">→</span>
              </button>
            </div>
            
            {/* Toggle pour masquer/afficher les mois précédents - Compact */}
            <div className="w-full max-w-2xl">
              <div className="flex items-center justify-center">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showPastMonths}
                    onChange={(e) => setShowPastMonths(e.target.checked)}
                    className="w-3 h-3 md:w-4 md:h-4 text-amber-500 bg-slate-700 border-slate-600 rounded focus:ring-amber-500 focus:ring-2"
                  />
                  <span className="text-[10px] md:text-xs font-medium text-slate-200">
                    Afficher jours passés
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>
        
        {/* Bouton Missions Récurrentes - Compact */}
        <div className="flex justify-center mt-2 md:mt-3">
          <button
            onClick={() => setShowRecurringModal(true)}
            className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-3 md:px-5 py-1.5 md:py-2.5 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all font-semibold shadow-md hover:shadow-lg flex items-center gap-1 md:gap-2 text-xs md:text-sm border border-purple-400/30"
            title="Créer des missions récurrentes"
          >
            🔄 <span className="hidden sm:inline">Paramétrer des </span>missions récurrentes
          </button>
        </div>
      </div>

      {/* Timeline Grid avec scroll horizontal amélioré */}
      <div className="card">
        <div 
          ref={scrollContainerRef}
          className="overflow-x-auto overflow-y-auto timeline-scroll-enhanced overscroll-x-contain"
          style={{
            cursor: 'grab',
            userSelect: 'none',
            WebkitOverflowScrolling: 'touch',
            scrollBehavior: 'smooth',
            scrollSnapType: 'x proximity',
            touchAction: 'auto', // Permet scroll horizontal ET vertical sur tactile
            maxHeight: '80vh', // Limite la hauteur pour permettre le scroll vertical
            overscrollBehavior: 'contain auto' // Contenir X, permettre Y de se propager
          }}
          onWheel={(e) => {
            // Si on scroll vers le haut et qu'on est déjà en haut du conteneur
            if (e.deltaY < 0 && scrollContainerRef.current) {
              const isAtTop = scrollContainerRef.current.scrollTop === 0;
              if (isAtTop) {
                // Ne pas empêcher la propagation - laisser le scroll remonter vers la page
                return;
              }
            }
            // Sinon, comportement normal (scroll interne)
          }}
          onMouseDown={(e) => {
            // Ignorer si on clique sur un bouton, select ou input
            if (e.target.tagName === 'BUTTON' || 
                e.target.tagName === 'SELECT' || 
                e.target.tagName === 'INPUT' ||
                e.target.tagName === 'TEXTAREA') {
              return;
            }
            
            const slider = scrollContainerRef.current;
            if (!slider) return;
            
            e.preventDefault();
            slider.style.cursor = 'grabbing';
            
            let startX = e.pageX - slider.offsetLeft;
            let scrollLeft = slider.scrollLeft;
            let isDragging = false;
            
            const handleMouseMove = (e) => {
              isDragging = true;
              const x = e.pageX - slider.offsetLeft;
              const walk = (x - startX) * 2.5; // Augmenté pour plus de réactivité
              slider.scrollLeft = scrollLeft - walk;
            };
            
            const handleMouseUp = () => {
              slider.style.cursor = 'grab';
              document.removeEventListener('mousemove', handleMouseMove);
              document.removeEventListener('mouseup', handleMouseUp);
              
              // Délai court pour distinguer clic simple du drag
              setTimeout(() => {
                isDragging = false;
              }, 100);
            };
            
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
          }}
        >
          <div className="min-w-max">
            {/* Header row with mission numbers */}
            <div className="flex border-b-2 border-gray-300 bg-gray-50 sticky top-0 z-10">
              <div 
                className="w-20 md:w-40 flex-shrink-0 p-1 md:p-2 text-[10px] md:text-sm font-semibold border-r-2 border-gray-300 bg-gray-50 sticky left-0 z-20 shadow-[2px_0_5px_rgba(0,0,0,0.1)]"
                style={{ position: 'sticky', left: 0 }}
              >
                <span className="hidden md:inline">Jour / Mission</span>
                <span className="md:hidden">Date</span>
              </div>
              {missionSlots.map((slotNum) => (
                <div
                  key={slotNum}
                  className="w-28 md:w-40 flex-shrink-0 p-1 md:p-2 text-center text-[10px] md:text-sm font-semibold border-r border-gray-200 bg-gray-50"
                >
                  M{slotNum}
                </div>
              ))}
            </div>

          {/* Days rows grouped by month */}
          {groupDaysByMonth(weekDays).map((monthGroup, monthIndex) => {
            const monthKey = `${monthGroup.year}-${monthGroup.monthIndex}`;
            const isExpanded = expandedMonths.has(monthKey);
            const today = getTodayParis();
            const todayYear = today.getFullYear();
            const todayMonth = today.getMonth();
            
            // Déterminer si ce mois doit être affiché
            const isCurrentMonth = monthGroup.year === todayYear && monthGroup.monthIndex === todayMonth;
            const isPastMonth = monthGroup.year < todayYear || (monthGroup.year === todayYear && monthGroup.monthIndex < todayMonth);
            
            // Si showPastMonths est false, masquer les mois précédents et les dates antérieures du mois en cours
            if (!showPastMonths && isPastMonth) {
              return null; // Ne pas afficher ce mois
            }
            
            console.log(`🔍 Mois: ${monthGroup.monthName} ${monthGroup.year}, clé: ${monthKey}, expanded: ${isExpanded}`);
            
            return (
              <div key={monthKey}>
                {/* Month header - Design Premium Responsive */}
                <div className="flex border-b-2 border-slate-300 bg-gradient-to-r from-slate-100 via-slate-50 to-slate-100 shadow-sm">
                  <button
                    onClick={() => toggleMonth(monthKey)}
                    className="w-full flex items-center justify-between p-2 md:p-4 hover:bg-slate-200/50 transition-all duration-200"
                  >
                    <div className="flex items-center space-x-2 md:space-x-4">
                      <span className="text-base md:text-xl font-bold text-amber-400 transition-transform duration-200" style={{
                        transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'
                      }}>
                        ▼
                      </span>
                      <span className="text-lg md:text-3xl font-semibold text-white tracking-tight" style={{ 
                        letterSpacing: '-0.015em',
                        fontWeight: '600'
                      }}>
                        {monthGroup.monthName.charAt(0).toUpperCase() + monthGroup.monthName.slice(1)}
                      </span>
                      <span className="text-base md:text-2xl font-semibold text-slate-200">
                        {monthGroup.year}
                      </span>
                      <span className="text-[10px] md:text-sm font-medium text-slate-600 bg-white px-1.5 md:px-3 py-0.5 md:py-1 rounded-full shadow-sm">
                        {monthGroup.days.length}j
                      </span>
                    </div>
                  </button>
                </div>

                {/* Days in this month */}
                {isExpanded && monthGroup.days.map((date, dayIndex) => {
                  const dayNameFr = getDayNameFrench(date).charAt(0).toUpperCase() + getDayNameFrench(date).slice(1);
                  const isToday = date.toDateString() === todayStr;
                  const dayMissions = getMissionsForDate(date);
                  
                  // Si showPastMonths est false et que nous sommes dans le mois en cours, masquer les dates antérieures à J-1
                  if (!showPastMonths && isCurrentMonth) {
                    const yesterday = addDays(today, -1);
                    if (date < yesterday) {
                      return null; // Ne pas afficher ce jour
                    }
                  }
                  
                  const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                  console.log(`🎯 Render ${dateStr}:`, {
                    totalMissions: dayMissions.length,
                    hasDivers: dayMissions.filter(m => !m.housing).length,
                    hasHousings: dayMissions.filter(m => m.housing).length
                  });
                  
                  return (
                    <div
                      key={date.toISOString()}
                      ref={(el) => {
                        // Ref pour aujourd'hui
                        if (isToday) todayRef.current = el;
                        // Ref pour chaque date (pour scroll après ajout)
                        dateRefs.current[dateStr] = el;
                      }}
                      className={`flex border-b border-gray-200 ${
                        isToday ? 'bg-gradient-to-r from-amber-50 to-amber-100 border-amber-400 border-l-4' : ''
                      }`}
                    >
                      {/* Day label with month - Design Premium Responsive */}
                      <div 
                        className={`w-20 md:w-40 flex-shrink-0 p-1.5 md:p-4 border-r border-slate-200 sticky left-0 z-10 shadow-sm text-center transition-all ${
                          isToday ? 'bg-gradient-to-br from-amber-50 via-amber-100 to-amber-50 border-l-4 border-amber-500 shadow-md' : 'bg-gradient-to-br from-slate-50 to-white'
                        }`}
                        style={{ position: 'sticky', left: 0 }}
                      >
                        {/* Version Mobile - Ultra Compacte */}
                        <div className="md:hidden">
                          <div className="flex items-center justify-center gap-0.5 mb-0.5">
                            {isToday && <span className="text-amber-600 text-[10px] font-bold animate-pulse">▶</span>}
                            <span className={`text-[10px] font-bold uppercase ${
                              isToday ? 'text-amber-900' : 'text-slate-700'
                            }`}>
                              {dayNameFr.substring(0, 3)}
                            </span>
                          </div>
                          <div className={`text-lg font-bold ${
                            isToday ? 'text-amber-600' : 'text-slate-900'
                          }`}>
                            {String(date.getDate()).padStart(2, '0')}
                          </div>
                          <button
                            onClick={() => handleOpenModal(date)}
                            className={`mt-1 w-full text-[9px] font-semibold py-1 px-1 rounded transition-all ${
                              isToday 
                                ? 'bg-amber-600 text-white' 
                                : 'bg-slate-600 text-white'
                            }`}
                          >
                            ➕
                          </button>
                        </div>
                        
                        {/* Version Desktop */}
                        <div className="hidden md:block">
                          <div className="flex items-center justify-center gap-2 mb-2">
                            {isToday && <span className="text-amber-600 text-base font-bold animate-pulse">▶</span>}
                            <span className={`text-sm font-bold tracking-wide uppercase ${
                              isToday ? 'text-amber-900' : 'text-slate-700'
                            }`} style={{ letterSpacing: '0.05em' }}>
                              {dayNameFr}
                            </span>
                          </div>
                          <div className={`text-3xl font-bold mb-2 ${
                            isToday ? 'text-amber-600' : 'text-slate-900'
                          }`}>
                            {String(date.getDate()).padStart(2, '0')}
                          </div>
                          <div className={`text-xs font-semibold uppercase tracking-wider ${
                            isToday ? 'text-amber-700' : 'text-slate-600'
                          }`} style={{ letterSpacing: '0.08em' }}>
                            {getMonthNameFrench(date).substring(0, 4).toUpperCase()}
                          </div>
                          <div className={`text-xs font-medium ${
                            isToday ? 'text-amber-600' : 'text-slate-600'
                          }`}>
                            {monthGroup.year}
                          </div>
                          <button
                            onClick={() => handleOpenModal(date)}
                            className={`mt-3 w-full text-xs font-semibold py-2 px-2 rounded-lg transition-all duration-200 shadow-sm ${
                              isToday 
                                ? 'bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white shadow-md hover:shadow-lg' 
                                : 'bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white hover:shadow-md'
                            }`}
                            title="Ajouter une mission"
                          >
                            ➕ Ajouter
                          </button>
                        </div>
                      </div>

                      {/* Mission slots - Responsive */}
                      {missionSlots.map((slotNum) => {
                        const missionData = dayMissions[slotNum - 1];
                        
                        return (
                          <div
                            key={slotNum}
                            className="w-28 md:w-40 flex-shrink-0 border-r border-gray-200 min-h-5 p-0.5 md:p-1.5"
                          >
                            {missionData ? (() => {
                              const cardStyle = getEmployeeCardStyle(missionData.mission.employee_id, { hasHousing: !!missionData.housing });
                              return (
                              <div className={`${cardStyle.bg} ${cardStyle.border} shadow-sm border rounded-lg md:rounded-xl p-1 md:p-2.5 text-[9px] md:text-xs h-full hover:shadow-lg hover:scale-[1.02] transition-all duration-200 relative`}>
                                <div className="flex justify-between items-start mb-0.5 md:mb-1">
                                  <div className="flex items-center gap-0.5 md:gap-1">
                                    <button
                                      onClick={() => handleEditMission(missionData)}
                                      className={`text-gray-600 ${cardStyle.hover} text-[9px] md:text-xs flex items-center gap-0.5 md:gap-1`}
                                      title="Modifier/Déplacer/Supprimer"
                                    >
                                      ⚙️
                                    </button>
                                    {/* Badge source iCal */}
                                    {missionData.mission.source === 'ical_airbnb' && (
                                      <span 
                                        className="inline-flex items-center px-1 md:px-1.5 rounded text-[7px] md:text-[10px] font-bold bg-red-100 text-red-700"
                                        title="Mission créée depuis Airbnb"
                                      >
                                        Airbnb
                                      </span>
                                    )}
                                    {missionData.mission.source === 'ical_booking' && (
                                      <span 
                                        className="inline-flex items-center px-1 md:px-1.5 rounded text-[7px] md:text-[10px] font-bold bg-blue-100 text-blue-700"
                                        title="Mission créée depuis Booking.com"
                                      >
                                        Booking
                                      </span>
                                    )}
                                    {missionData.mission.ical_orphan && (
                                      <span 
                                        className="inline-flex items-center px-1 md:px-1.5 rounded text-[7px] md:text-[10px] font-bold bg-orange-100 text-orange-800"
                                        title="Réservation introuvable dans le calendrier iCal — à vérifier"
                                        data-testid={`ical-orphan-badge-${missionData.mission.id}`}
                                      >
                                        ⚠️ Résa à checker
                                      </span>
                                    )}
                                    {missionData.mission.en_binome && (
                                      <span 
                                        className="inline-flex items-center px-1 md:px-1.5 rounded text-[7px] md:text-[10px] font-bold bg-amber-100 text-amber-700"
                                        title="En binôme"
                                      >
                                        👥
                                      </span>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Si c'est un événement divers (pas de logement) */}
                                {!missionData.housing ? (
                                  <div>
                                    <div className="text-red-700 font-bold mb-0.5 md:mb-1 text-[9px] md:text-xs">
                                      📅 DIVERS
                                    </div>
                                    {missionData.employee && (
                                      <div className="text-gray-700 text-[9px] md:text-xs mb-0.5 md:mb-1 truncate">
                                        👤 {missionData.employee.prenom}
                                      </div>
                                    )}
                                    {missionData.mission.notes && (
                                      <div className="text-blue-600 text-[8px] md:text-xs font-medium mt-0.5 md:mt-1 line-clamp-2">
                                        {missionData.mission.notes}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div>
                                    {/* Sélecteur d'employée - Compact sur mobile */}
                                    <div className="mb-0.5 md:mb-1">
                                      <select
                                        value={missionData.mission.employee_id || ''}
                                        onChange={(e) => handleEmployeeChange(missionData.mission.id, e.target.value)}
                                        className="w-full text-[8px] md:text-xs p-0.5 border border-blue-400 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                                        title="Assigner une employée"
                                      >
                                        <option value="">👤 Non assignée</option>
                                        {employees.map((emp) => (
                                          <option key={emp.id} value={emp.id}>
                                            {emp.prenom} {emp.nom.charAt(0)}.
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                    
                                    {/* Pastille de type de lieu avec pseudo */}
                                    {missionData.housing.pseudo && (
                                      <div className="flex items-center gap-0.5 md:gap-1 mb-0.5">
                                        <div className="text-purple-600 font-semibold text-[9px] md:text-xs truncate flex items-center gap-0.5 md:gap-1">
                                          🎤 {missionData.housing.pseudo}
                                        </div>
                                      </div>
                                    )}
                                    
                                    <div className="text-gray-700 text-[8px] md:text-xs truncate mb-0.5">
                                      {missionData.housing.adresse}
                                    </div>
                                    
                                    <div className="text-gray-600 text-[8px] md:text-xs truncate hidden md:block">
                                      {missionData.housing.ville}
                                    </div>
                                    
                                    {/* Informations complémentaires (précommandes) */}
                                    {missionData.mission.informations_complementaires && (
                                      <div className="text-blue-600 text-[8px] md:text-xs font-medium mt-0.5 md:mt-1 line-clamp-1 md:line-clamp-2 bg-blue-50 p-0.5 md:p-1 rounded">
                                        📦 {missionData.mission.informations_complementaires}
                                      </div>
                                    )}
                                    
                                    {/* Notes de la mission */}
                                    {missionData.mission.notes && (
                                      <div className="text-amber-700 text-[8px] md:text-xs font-medium mt-0.5 md:mt-1 line-clamp-1 md:line-clamp-2 bg-amber-50 p-0.5 md:p-1 rounded">
                                        📝 {missionData.mission.notes}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                              );
                            })() : (
                              <div className="h-full flex items-center justify-center text-gray-300">
                                {/* Case vide */}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
        
      </div>
      </div>

      {/* Modal de création de mission */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                ➕ Nouvelle Mission - {formatDateFR(selectedDate)}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ❌
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Case à cocher "Divers" */}
              <div className="bg-gray-50 border border-gray-300 rounded-lg p-3">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_divers}
                    onChange={(e) => setFormData({
                      ...formData, 
                      is_divers: e.target.checked,
                      housing_id: e.target.checked ? "" : formData.housing_id
                    })}
                    className="w-5 h-5 text-red-600 border-gray-300 rounded focus:ring-red-500"
                  />
                  <div>
                    <span className="text-sm font-semibold text-gray-900">
                      📅 DIVERS (Rendez-vous personnel, professionnel, etc.)
                    </span>
                    <p className="text-xs text-gray-600 mt-1">
                      Cochez cette case pour ajouter un événement qui n'est pas lié à un logement
                    </p>
                  </div>
                </label>
              </div>

              {/* Logement - seulement si ce n'est pas "divers" */}
              {!formData.is_divers && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Logement *
                  </label>
                  <select
                    required
                    value={formData.housing_id}
                    onChange={(e) => handleHousingChange(e.target.value)}
                    className="form-select"
                  >
                    <option value="">Sélectionner un logement</option>
                    {[...housings]
                      .sort((a, b) => {
                        const clientA = clients.find(c => c.id === a.client_id);
                        const clientB = clients.find(c => c.id === b.client_id);
                        const prenomA = clientA ? clientA.prenom.toLowerCase() : "";
                        const prenomB = clientB ? clientB.prenom.toLowerCase() : "";
                        return prenomA.localeCompare(prenomB);
                      })
                      .map((housing) => {
                        const client = clients.find(c => c.id === housing.client_id);
                        const clientName = client ? `${client.prenom} ${client.nom}` : "Client inconnu";
                        const displayName = housing.pseudo 
                          ? `🎤 ${housing.pseudo}` 
                          : `${housing.adresse}, ${housing.ville}`;
                        return (
                          <option key={housing.id} value={housing.id}>
                            {clientName} - {displayName}
                          </option>
                        );
                      })}
                  </select>
                </div>
              )}

              {/* Employée - disponible pour tous les types de missions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Employée {formData.is_divers ? '(optionnel)' : ''}
                </label>
                <select
                  value={formData.employee_id}
                  onChange={(e) => setFormData({...formData, employee_id: e.target.value})}
                  className="form-select"
                >
                  <option value="">À assigner plus tard</option>
                  {employees.map((employee) => (
                    <option key={employee.id} value={employee.id}>
                      {employee.prenom} {employee.nom}
                    </option>
                  ))}
                </select>
              </div>

              {/* Prix - seulement si ce n'est pas "divers" */}
              {!formData.is_divers && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prix M&M Conciergerie (€)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.prix}
                      onChange={(e) => setFormData({...formData, prix: e.target.value})}
                      className="form-input"
                      placeholder="Rempli automatiquement"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prix Intervenant (€)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.prix_intervenant}
                      onChange={(e) => setFormData({...formData, prix_intervenant: e.target.value})}
                      className="form-input"
                      placeholder="Rempli automatiquement"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes {formData.is_divers ? '(Description du rendez-vous) *' : ''}
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  required={formData.is_divers}
                  rows={3}
                  className="form-textarea"
                  placeholder={
                    formData.is_divers 
                      ? "Ex: Rendez-vous docteur à 14h, Réunion professionnelle..."
                      : "Notes additionnelles..."
                  }
                />
                {formData.is_divers && (
                  <p className="text-xs text-red-600 mt-1">
                    * Pour un événement DIVERS, précisez le type de rendez-vous dans les notes
                  </p>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  ✅ Créer la mission
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal d'édition/déplacement de mission */}
      {showEditModal && editingMission && (
        <div className="modal-overlay">
          <div className="modal-content max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                ⚙️ Gérer la Mission
              </h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ❌
              </button>
            </div>

            <div className="space-y-4">
              {/* Informations de la mission */}
              <div className={`${
                !editingMission.housing ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'
              } border rounded-lg p-4`}>
                <div className="font-semibold text-blue-900 mb-2">
                  {editingMission.employee ? 
                    `${editingMission.employee.prenom} ${editingMission.employee.nom}` : 
                    "Non assignée"}
                </div>

                {editingMission.mission.ical_orphan && (
                  <div className="flex flex-wrap items-center gap-2 bg-orange-50 border border-orange-200 rounded px-2 py-1.5 mb-2 text-sm text-orange-900" data-testid="ical-orphan-edit-notice">
                    <span>⚠️ Résa à checker : la réservation n'existe plus dans le calendrier iCal.</span>
                    <button
                      onClick={async () => {
                        try {
                          await axios.put(`${API}/missions/${editingMission.mission.id}/ical-orphan/dismiss`);
                          setEditingMission(em => ({ ...em, mission: { ...em.mission, ical_orphan: false } }));
                          loadData();
                        } catch (e) {
                          showToast("Erreur", "error");
                        }
                      }}
                      className="ml-auto px-2 py-0.5 rounded bg-white border border-orange-300 hover:bg-orange-100 text-xs font-semibold"
                      data-testid="ical-orphan-keep-btn"
                    >
                      Garder (retirer l'alerte)
                    </button>
                  </div>
                )}
                
                {/* Si c'est un événement DIVERS */}
                {!editingMission.housing ? (
                  <div>
                    <div className="text-red-700 font-bold mb-2">
                      📅 ÉVÉNEMENT DIVERS
                    </div>
                    {editingMission.mission.notes && (
                      <div className="text-blue-600 text-sm font-medium mt-2 mb-2">
                        {editingMission.mission.notes}
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    {editingMission.housing.pseudo && (
                      <div className="text-purple-600 font-medium">
                        🎤 {editingMission.housing.pseudo}
                      </div>
                    )}
                    <div className="text-gray-700">
                      {editingMission.housing.adresse}, {editingMission.housing.ville}
                    </div>
                  </div>
                )}
                
                <div className="text-gray-600 text-sm mt-1">
                  Date actuelle : {formatDateFR(editingMission.mission.date_mission)}
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-3">
                {/* Modifier les notes */}
                <div className="border-t pt-3">
                  <h4 className="font-medium text-gray-900 mb-3">📝 Notes de la mission</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Notes
                      </label>
                      <textarea
                        value={editedNotes}
                        onChange={(e) => setEditedNotes(e.target.value)}
                        placeholder="Ajoutez des notes sur cette mission..."
                        className="form-textarea w-full h-24"
                        rows="3"
                      />
                    </div>
                    <button
                      onClick={handleSaveNotes}
                      className="btn-primary w-full"
                    >
                      💾 Enregistrer les notes
                    </button>
                  </div>
                </div>

                <div className="border-t pt-3">
                  <h4 className="font-medium text-gray-900 mb-3">👥 Binôme (formation)</h4>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingMission.mission.en_binome || false}
                        onChange={async (e) => {
                          const checked = e.target.checked;
                          const missionId = editingMission.mission.id;
                          const prev = [...missions];
                          setMissions(ms => ms.map(m => {
                            if ((m.mission?.id || m.id) === missionId) {
                              return { ...m, mission: { ...m.mission, en_binome: checked, binome_employee_id: checked ? m.mission.binome_employee_id : null } };
                            }
                            return m;
                          }));
                          setEditingMission(em => ({ ...em, mission: { ...em.mission, en_binome: checked, binome_employee_id: checked ? em.mission.binome_employee_id : null } }));
                          try {
                            await axios.put(`${API}/missions/${missionId}`, { en_binome: checked, binome_employee_id: checked ? editingMission.mission.binome_employee_id : null });
                          } catch (err) {
                            setMissions(prev);
                            showToast("Erreur serveur", "error");
                          }
                        }}
                        className="rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                        data-testid="binome-checkbox-weekly"
                      />
                      <span className="text-sm font-medium text-amber-900">En binôme</span>
                    </label>
                    {editingMission.mission.en_binome && (
                      <div className="space-y-2">
                        <select
                          value={editingMission.mission.binome_employee_id || ''}
                          onChange={async (e) => {
                            const binomeId = e.target.value || null;
                            const missionId = editingMission.mission.id;
                            const prev = [...missions];
                            setMissions(ms => ms.map(m => {
                              if ((m.mission?.id || m.id) === missionId) {
                                return { ...m, mission: { ...m.mission, binome_employee_id: binomeId } };
                              }
                              return m;
                            }));
                            setEditingMission(em => ({ ...em, mission: { ...em.mission, binome_employee_id: binomeId } }));
                            try {
                              await axios.put(`${API}/missions/${missionId}`, { binome_employee_id: binomeId });
                            } catch (err) {
                              setMissions(prev);
                              showToast("Erreur serveur", "error");
                            }
                          }}
                          className="form-select text-sm w-full"
                          data-testid="binome-employee-select-weekly"
                        >
                          <option value="">-- Choisir le binôme --</option>
                          {employees.filter(emp => emp.id !== editingMission.mission.employee_id).map((emp) => (
                            <option key={emp.id} value={emp.id}>
                              {emp.prenom} {emp.nom} {emp.type_employe === 'proprietaire' ? '(Gérant)' : ''}
                            </option>
                          ))}
                        </select>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs text-amber-800 mb-0.5">Prix employé principal (€)</label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              defaultValue={editingMission.mission.prix_intervenant || ''}
                              onChange={(e) => setEditingMission(em => ({ ...em, _prixPrincipal: e.target.value }))}
                              className="form-input text-sm"
                              placeholder="Prix libre"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-amber-800 mb-0.5">Prix binôme (€)</label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              defaultValue={editingMission.mission.prix_binome || ''}
                              onChange={(e) => setEditingMission(em => ({ ...em, _prixBinome: e.target.value }))}
                              className="form-input text-sm"
                              placeholder="Prix libre"
                            />
                          </div>
                        </div>
                        <button
                          onClick={async () => {
                            const missionId = editingMission.mission.id;
                            const prixP = editingMission._prixPrincipal !== undefined ? editingMission._prixPrincipal : editingMission.mission.prix_intervenant;
                            const prixB = editingMission._prixBinome !== undefined ? editingMission._prixBinome : editingMission.mission.prix_binome;
                            try {
                              await axios.put(`${API}/missions/${missionId}`, {
                                prix_intervenant: prixP ? parseFloat(prixP) : null,
                                prix_binome: prixB ? parseFloat(prixB) : null
                              });
                              setEditingMission(em => ({
                                ...em,
                                mission: { ...em.mission, prix_intervenant: prixP ? parseFloat(prixP) : null, prix_binome: prixB ? parseFloat(prixB) : null }
                              }));
                              showToast("Prix enregistrés", "success");
                            } catch (err) {
                              showToast("Erreur serveur", "error");
                            }
                          }}
                          className="w-full py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-bold transition-all"
                        >
                          ✅ Valider les prix
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="border-t pt-3">
                  <h4 className="font-medium text-gray-900 mb-3">📅 Déplacer la mission</h4>
                  <div className="flex items-end space-x-3">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nouvelle date
                      </label>
                      <input
                        type="date"
                        value={moveToDate}
                        onChange={(e) => setMoveToDate(e.target.value)}
                        className="form-input"
                      />
                    </div>
                    <button
                      onClick={handleMoveMission}
                      className="btn-primary"
                      disabled={!moveToDate || moveToDate === editingMission.mission.date_mission}
                    >
                      📆 Déplacer
                    </button>
                  </div>
                </div>

                <div className="border-t pt-3">
                  <h4 className="font-medium text-gray-900 mb-3">🗑️ Supprimer la mission</h4>
                  <button
                    onClick={() => handleDeleteMission(editingMission.mission.id)}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 w-full"
                  >
                    🗑️ Supprimer définitivement
                  </button>
                </div>
              </div>

              <div className="flex justify-end pt-4 border-t">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="btn-secondary"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de confirmation centré */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({...confirmModal, isOpen: false})}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText="Continuer quand même"
        cancelText="Annuler"
      />
      
      {/* Toast de notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Modal Missions Récurrentes */}
      <RecurringMissionModal
        isOpen={showRecurringModal}
        onClose={() => setShowRecurringModal(false)}
        clients={clients}
        housings={housings}
        employees={employees}
        onSubmit={handleCreateRecurringMissions}
      />
    </div>
  );
};

// Styles CSS pour optimisation scroll et responsive
const enhancedStyles = `
/* Scroll horizontal ET vertical amélioré */
.timeline-scroll-enhanced {
  overflow-x: auto;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
  cursor: grab;
  /* Barre de scroll toujours visible et plus grosse */
  scrollbar-width: auto;
  scrollbar-color: #3B82F6 #E5E7EB;
  /* Support iOS Safari pour scroll momentum */
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}

/* Barre de scroll horizontale */
.timeline-scroll-enhanced::-webkit-scrollbar {
  height: 16px;
  background: #F3F4F6;
}

/* Barre de scroll verticale */
.timeline-scroll-enhanced::-webkit-scrollbar:vertical {
  width: 16px;
  background: #F3F4F6;
}

.timeline-scroll-enhanced::-webkit-scrollbar-track {
  background: #E5E7EB;
  border-radius: 8px;
  margin: 0 10px;
}

.timeline-scroll-enhanced::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #3B82F6 0%, #2563EB 100%);
  border-radius: 8px;
  border: 3px solid #E5E7EB;
  min-width: 60px;
  min-height: 60px;
}

.timeline-scroll-enhanced::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #2563EB 0%, #1D4ED8 100%);
  border: 2px solid #E5E7EB;
}

.timeline-scroll-enhanced::-webkit-scrollbar-thumb:active {
  background: #1D4ED8;
}

.timeline-scroll-enhanced:active {
  cursor: grabbing;
}

/* Colonne dates sticky (fixe à gauche) */
.sticky.left-0 {
  position: sticky;
  left: 0;
  z-index: 20;
  background: white;
  box-shadow: 3px 0 8px rgba(0, 0, 0, 0.15);
}

/* Optimisation mobile - scroll tactile fluide */
@media (max-width: 768px) {
  .timeline-scroll-enhanced {
    /* Optimisation pour le scroll tactile iOS */
    -webkit-overflow-scrolling: touch;
    scroll-behavior: smooth;
    /* Permet le scroll dans les deux directions */
    touch-action: auto !important;
    overscroll-behavior: contain;
  }
  
  /* Colonne dates reste sticky sur mobile */
  .sticky.left-0 {
    position: sticky !important;
    left: 0 !important;
    z-index: 20 !important;
    box-shadow: 2px 0 6px rgba(0, 0, 0, 0.1);
  }
  
  .mission-card {
    min-height: 44px !important;
    padding: 12px !important;
  }
  
  .timeline-day-header {
    font-size: 14px !important;
  }
  
  button {
    min-height: 44px !important;
    min-width: 44px !important;
  }
  
  .timeline-scroll-enhanced::-webkit-scrollbar {
    height: 10px;
    width: 10px;
  }
  
  .card-body {
    padding: 8px !important;
  }
  
  @media (max-width: 640px) {
    button {
      font-size: 12px !important;
      padding: 8px 12px !important;
    }
  }
}
`;

// Injecter les styles
if (typeof document !== 'undefined') {
  const styleId = 'weekly-timeline-enhanced-styles';
  if (!document.getElementById(styleId)) {
    const styleElement = document.createElement('style');
    styleElement.id = styleId;
    styleElement.textContent = enhancedStyles;
    document.head.appendChild(styleElement);
  }
}

export default WeeklyTimeline;
